import {useEffect, useLayoutEffect, useState} from "react";
import { toast } from "react-toastify";
import {
    CreateContractorEmployee
} from "../../../../../reduxToolkit/Contractor/ContractorApi";
import {getPhotoPicker} from '../../../../../components/PhotoPicker/SubComponent/Models/Models';
import {errorMsg, validateMinLengthTrimmed} from "../../../../../Apis/functions";
import {CONTRACTOR_EMPLOYEE_DATA, EXTRA_DATA} from '../../../Company/Employees/Model/Models';
import { PASSWORD_MIN_LENGTH } from "../../../../../constant/variable";
import { useSelector } from "react-redux";
import { GetUserRestriction } from "../../../../../reduxToolkit/restrictions/RestrictionApi";
import HandleChange from "../../../../../Helpers/HandleChange";

/**
 * @Email   : kpozos@ibl.mx
 * @Author  : Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer
 */

/**
 * Hook that encapsulates all the state and behaviour
 * for creating a new contractor employee.
 *
 * All parameters are injected from the consuming component so that
 * the hook stays framework‑agnostic and easy to unit‑test.
 */
export const ContractorCreateEmployeeApp = (
    {
        t,                       // i18next translation function
        permission,
        dispatch,                // Redux dispatch
        setEmptyFields,          // Setter for emptyFields
        isValidEmail,            // Email validation flag provided by the form
        isValidPhoneNumber,      // Phone validation flag provided by the form
        isValidDob,              // Date‑of‑birth validation flag
        isValidPassword,
        reset,
        photoPicker,
        setSecretModal,
        setSecretKey,
        setGeneratedPassword,
        passwordGenerator,
        validateEmail,
        validatePhone,
        validatePassword,
        isValidDocumentId,
        extraData,
        extraDataEnabled,
        setExtraData,
        setExtraDataEnabled,
        headersList
    }
) => {

    /** Local form state for the new employee */
    const [user, setUser] = useState({...CONTRACTOR_EMPLOYEE_DATA});
    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(0);
    
    useEffect(() => {

            dispatch(GetUserRestriction()).then(
                ({
                    payload: {
                        data: { data, success },
                    },
                }) => {
                    if (success === true) {
                        setExtraDataEnabled( data?.extraDataContractor);
                    }
                },
            );
        }, []);

    const  STEP_EMPLOYEE_CREATE = (extraDataEnabled) => {
        if (extraDataEnabled){
            return [t('details'),t('other_details'), t('add_photo'),t('summary')];
        }
        return [t('details'), t('add_photo'),t('summary')];
    }

    useLayoutEffect(() => {
        setSteps(STEP_EMPLOYEE_CREATE(extraDataEnabled) )
    }, [extraDataEnabled, t, permission]);


    /**
     * Function to clear the contract form
     */
    const handleClearAll = () => {
        reset();
        setUser({...CONTRACTOR_EMPLOYEE_DATA});
    };

    /**
     * Main “Save” button handler.
     * Validates required fields and calls the creation helper
     * if everything is present and valid.
     */
    const FormHandler = () => {
        const hasInvalidField = !isValidDob || !isValidPhoneNumber || !isValidEmail || !isValidPassword;
        if (!user.isValidEmployee() || hasInvalidField) {
            setEmptyFields(true);
            return;
        }

        setEmptyFields(false);
        createEmployee();
    };

    const handleValidateSteps = (step) => {
        switch (step) {
            case 0: {
                const isPasswordValid = validateMinLengthTrimmed(user.password, PASSWORD_MIN_LENGTH);
                const hasInvalidField = !isValidDob || !isValidDocumentId || !validateEmail(user.email) || !validatePhone(user.phoneNumber) || !validatePassword(user.password);
                if (!user.isValidEmployee() || hasInvalidField) {
                    setEmptyFields(true);
                    return false;
                }
                return true;
            }
            default:
                return true;
        }
    }

    /**
     * Dispatches the API call to create the employee,
     * then refreshes contractor info and redirects back to the listing.
     */
    const createEmployee = () => {
        let imageFile = getPhotoPicker(photoPicker);
        const password = user?.password?.trim() !== "" ? user?.password: passwordGenerator()

        dispatch(
            CreateContractorEmployee({
                data: user.toCreateUpdate(password, extraData),  // Flattened payload from the model
                file: imageFile               // Picture file, if any
            })
        ).then(({payload:{data:{data, success, code}}}) => {
            if (success) {
                toast.success(t('has_been_created_successfully'))
                setEmptyFields(false);
                setSecretModal(true);
                setSecretKey(data.secret);
                setGeneratedPassword(user?.password?.trim() !== "" ? null: password);
            }else errorMsg(code);
        });
    };

    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    const handleClearExtraData = () => {
        setExtraData({...EXTRA_DATA});
    }

   const { handleChange } = HandleChange({
    model: extraData,
    setModel: setExtraData,
    });

    /* Expose state and handlers to the UI layer */
    return {
        user,
        setUser,
        FormHandler,
        steps,
        activeStep, setActiveStep,
        handleStepChange,
        handleClearAll,
        handleValidateSteps,
        handleChange,
        handleClearExtraData,
        
    };
};
